<template>
  <div>
    <transition name="fade">
      <v-overlay :z-index="201" v-if="showEditPicks"></v-overlay>
    </transition>
    <v-dialog persistent autofocus :value="showEditPicks" hide-overlay>
      <v-card class="unmarkable">
        <v-card-title>
          <v-icon>
            $vuetify.icons.editPicks
          </v-icon>
          &nbsp; Edit picks
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-tabs centered grow v-model="currentTab">
            <v-tab key="addPicks" @click="focus = 'editPickInsertFromPickNr'"
              >Insert picks</v-tab
            >
            <v-tab key="removePicks" @click="focus = 'editPickRemovePickNr'"
              >Remove picks</v-tab
            >
            <v-tabs-items v-model="currentTab">
              <v-tab-item key="addPicks">
                <v-card flat>
                  <v-card-text>
                    <v-radio-group v-model="insertLocation" mandatory>
                      <v-radio
                        label="Insert before"
                        value="insertBefore"
                        on-icon="radio_button_checked"
                        off-icon="radio_button_unchecked"
                      ></v-radio>
                      <v-radio
                        label="Insert after"
                        value="insertAfter"
                        on-icon="radio_button_checked"
                        off-icon="radio_button_unchecked"
                      ></v-radio>
                    </v-radio-group>
                    <v-text-field
                      :label="`Pick #`"
                      :value="pickNr"
                      @input="pickNrInput"
                      :error-messages="pickNrError"
                      ref="editPickInsertFromPickNr"
                    ></v-text-field>
                    <v-text-field
                      label="# of picks"
                      :value="pickAmount"
                      @input="pickAmountInput"
                      :error-messages="pickAmountError"
                      ref="editPickInsertAmount"
                    ></v-text-field>
                  </v-card-text>
                </v-card>
              </v-tab-item>
              <v-tab-item key="removePicks">
                <v-card flat>
                  <v-card-text v-if="!isSelectionEmpty && simpleDelete">
                    {{ selectedPicks }}
                    {{ selectedPicks > 1 ? "picks" : "pick" }} from selection
                    will be removed.
                    <v-btn
                      text
                      color="secondary darken-1"
                      :style="{ display: 'block', margin: 'auto' }"
                      @click="simpleDelete = false"
                      >Specify range</v-btn
                    >
                  </v-card-text>
                  <v-card-text v-else>
                    <v-text-field
                      label="Start at pick #"
                      :value="pickNr"
                      @input="pickNrInput"
                      :error-messages="pickNrError"
                      ref="editPickRemovePickNr"
                    ></v-text-field>
                    <v-text-field
                      label="# of picks"
                      :value="pickAmount"
                      @input="pickAmountInput"
                      :error-messages="pickAmountError"
                      ref="editPickRemoveAmount"
                    ></v-text-field>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-tabs>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" text @click="toggleShowEditPicks">Cancel</v-btn>
          <v-btn
            color="secondary darken-1"
            text
            @click="applyChanges"
            :disabled="!canApplyChanges"
            >{{ applyText }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { notificationMixin } from "../mixins/notificationMixin.js";
import {
  INSERT_PICKS,
  REMOVE_PICKS,
  REMOVE_PICKS_FROM_SELECTION
} from "../../store/modules/uptData/constants";
import { TOGGLE_SHOW_EDIT_PICKS } from "../../store/modules/showComponents/constants";
import uptFile from "../utils/nonReactiveState/uptFile";
import selection from "../utils/nonReactiveState/selection";

const _ = require("lodash");
const MAX_PICKS = 100000;

const getInitialState = () => {
  return {
    currentTab: null,
    insertLocation: "insertAfter",
    pickNr: 1,
    pickNrError: "",
    pickAmount: 1,
    pickAmountError: "",
    simpleDelete: true,
    focus: false,
    selection: []
  };
};

export default {
  name: "EditPicks",

  mixins: [notificationMixin],

  data: getInitialState,

  computed: {
    showEditPicks() {
      return this.$store.state.showComponents.showEditPicks;
    },
    applyText() {
      switch (this.currentTab) {
        case 0:
          return "Insert picks";
        case 1:
          return "Remove picks";
        default:
          return "Apply";
      }
    },
    canApplyChanges() {
      switch (this.currentTab) {
        case 0:
          return !this.pickNrError && !this.pickAmountError;
        case 1:
          if (!this.isSelectionEmpty && this.simpleDelete) {
            return (
              this.selectedPicks + 2 <= uptFile.pickData.driveData.data.length
            );
          } else {
            return !this.pickNrError && !this.pickAmountError;
          }
        default:
          return false;
      }
    },
    isSelectionEmpty() {
      return _.isEmpty(this.selection);
    },
    selectedPicks() {
      return _.uniqBy(this.selection, s => s[0]).length;
    },
    aboveOrBelow() {
      switch (this.insertLocation) {
        case "insertAfter":
          return "below";
        case "insertBefore":
          return "above";
        default:
          return "???";
      }
    }
  },

  updated: function() {
    if (this.focus) {
      this.focusInput(this.focus);
    }
  },

  watch: {
    showEditPicks(newValue, oldValue) {
      if (newValue && !oldValue) {
        Object.assign(this, getInitialState());
        if (!_.isEmpty(selection.selectionList)) {
          this.pickNr = _.min(selection.selectionList.map(s => s[0])) + 1;
        }
        this.pickNrInput(this.pickNr);
        this.pickAmountInput(this.pickAmount);
        this.focus = "editPickInsertFromPickNr";
        this.selection = selection.selectionList;
      }
    },
    currentTab() {
      this.pickNrInput(this.pickNr);
      this.pickAmountInput(this.pickAmount);
    }
  },

  methods: {
    toggleShowEditPicks() {
      this.$store.dispatch(TOGGLE_SHOW_EDIT_PICKS);
    },
    applyChanges() {
      if (this.canApplyChanges) {
        switch (this.currentTab) {
          case 0:
            if (this.insertLocation === "insertAfter") {
              this.$store.dispatch(INSERT_PICKS, {
                index: this.pickNr,
                quantity: this.pickAmount
              });
            } else if (this.insertLocation === "insertBefore") {
              this.$store.dispatch(INSERT_PICKS, {
                index: this.pickNr - 1,
                quantity: this.pickAmount
              });
            }
            break;
          case 1:
            if (!this.isSelectionEmpty && this.simpleDelete) {
              this.$store.dispatch(REMOVE_PICKS_FROM_SELECTION);
            } else {
              this.$store.dispatch(REMOVE_PICKS, {
                index: this.pickNr - 1,
                quantity: this.pickAmount
              });
            }
            break;
        }
      } else {
        this.errorNotification("Failed to edit picks.");
      }
      this.toggleShowEditPicks();
    },
    pickNrInput(newPickNr) {
      const newPickNumber = _.toNumber(newPickNr);
      if (!_.isNaN(newPickNumber)) {
        this.pickNr = newPickNumber;
        if (
          _.isInteger(newPickNumber) &&
          newPickNumber > 0 &&
          newPickNr <= uptFile.pickData.driveData.data.length
        ) {
          this.pickNrError = "";
        } else {
          this.pickNrError = "No valid pick number";
        }
      } else {
        this.pickNrError = "Must be a pick number";
      }
      this.pickAmountInput(this.pickAmount);
    },
    pickAmountInput(newPickAmount) {
      const pickAmount = _.toNumber(newPickAmount);
      const nrOfPicks = uptFile.pickData.driveData.data.length;
      if (!_.isNaN(pickAmount)) {
        this.pickAmount = pickAmount;
        if (this.currentTab === 0) {
          if (
            _.isInteger(pickAmount) &&
            pickAmount > 0 &&
            pickAmount < MAX_PICKS
          ) {
            this.pickAmountError = "";
          } else {
            this.pickAmountError = `No valid pick amount (must be integer and smaller than ${MAX_PICKS})`;
          }
        } else if (this.currentTab === 1) {
          if (!_.isInteger(pickAmount)) {
            this.pickAmountError = "Pick amount must be an integer";
          } else if (pickAmount <= 0) {
            this.pickAmountError = "Must be greater than 0";
          } else if (
            pickAmount >
            nrOfPicks - _.clamp(this.pickNr, 3, Infinity) + 1
          ) {
            this.pickAmountError = `Can not remove more picks than ${nrOfPicks -
              _.clamp(this.pickNr, 3, Infinity) +
              1}`;
          } else if (nrOfPicks - pickAmount < 2) {
            this.pickAmountError = "Total remaining picks must be > 2";
          } else {
            this.pickAmountError = "";
          }
        }
      } else {
        this.pickAmountError = "Must be a pick amount (number)";
      }
    },
    async focusInput(input) {
      let numberOfTries = 0;
      while (numberOfTries < 5) {
        numberOfTries++;
        if (this.focus && this.$refs[input]) {
          this.focus = false;
          this.$nextTick(() => this.$refs[input].focus());
          break;
        } else {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
    }
  }
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style></style>
